import L from 'leaflet'

/**
 * Shared camera session for Control Center.
 * Keeps auto-framing from fighting the user or re-running on data polls.
 */
const session = {
  programmatic: false,
  userAdjustedSinceIntent: false,
  intentKey: null as string | null,
  clearProgrammaticTimer: 0 as ReturnType<typeof setTimeout> | 0,
}

/** Standard close-up for point entities (people, jobs, assets, place pins). */
export const ENTITY_POINT_ZOOM = 14

export type CameraFocusAnchor = {
  /** 0–1 fraction of map width from the left (0.5 = horizontal center). */
  x: number
  /** 0–1 fraction of map height from the top (0.25 = upper quarter). */
  y: number
}

export function beginCameraIntent(key: string): boolean {
  if (session.intentKey === key) return false
  session.intentKey = key
  session.userAdjustedSinceIntent = false
  return true
}

export function clearCameraIntent(): void {
  session.intentKey = null
  session.userAdjustedSinceIntent = false
}

export function userHasAdjustedCamera(): boolean {
  return session.userAdjustedSinceIntent
}

export function noteUserCameraGesture(): void {
  if (!session.programmatic) {
    session.userAdjustedSinceIntent = true
  }
}

function markProgrammatic(map: L.Map): void {
  session.programmatic = true
  if (session.clearProgrammaticTimer) clearTimeout(session.clearProgrammaticTimer)
  const done = () => {
    session.programmatic = false
    if (session.clearProgrammaticTimer) {
      clearTimeout(session.clearProgrammaticTimer)
      session.clearProgrammaticTimer = 0
    }
    map.off('moveend', done)
  }
  map.once('moveend', done)
  session.clearProgrammaticTimer = setTimeout(done, 900)
}

/**
 * Map center so `lat/lng` lands at the given screen-fraction anchor
 * (default center is `{ x: 0.5, y: 0.5 }`).
 */
export function mapCenterForFocusAnchor(
  map: L.Map,
  lat: number,
  lng: number,
  zoom: number,
  anchor: CameraFocusAnchor = { x: 0.5, y: 0.5 },
): L.LatLng {
  const size = map.getSize()
  const targetPx = map.project(L.latLng(lat, lng), zoom)
  const centerPx = L.point(
    targetPx.x - (anchor.x - 0.5) * size.x,
    targetPx.y - (anchor.y - 0.5) * size.y,
  )
  return map.unproject(centerPx, zoom)
}

/** True when the point sits inside the view with margin from the edges. */
export function isPointComfortablyVisible(
  map: L.Map,
  lat: number,
  lng: number,
  edgePad = -0.22,
): boolean {
  const target = L.latLng(lat, lng)
  return map.getBounds().pad(edgePad).contains(target)
}

/**
 * Frame a point entity to a standard close-up zoom.
 * Being "on screen" at state/county scale is not enough — zoom in to preferredZoom.
 * - Already at/above target zoom and pin comfortable → no-op (unless forceCenter)
 * - Already zoomed in but pin off-screen → pan only
 * - Otherwise → fly to preferredZoom centered on the pin
 * - forceCenter → always put the pin at the focus anchor (explicit focus / badge click)
 * - focusAnchor → place the pin at a screen fraction (e.g. mobile panel: y=0.25)
 */
export function ensurePointVisible(
  map: L.Map,
  lat: number,
  lng: number,
  opts?: {
    preferredZoom?: number
    forceCenter?: boolean
    focusAnchor?: CameraFocusAnchor
  },
): void {
  const preferredZoom = opts?.preferredZoom ?? ENTITY_POINT_ZOOM
  const forceCenter = Boolean(opts?.forceCenter)
  const focusAnchor = opts?.focusAnchor ?? { x: 0.5, y: 0.5 }
  const zoom = map.getZoom()
  const comfortable = isPointComfortablyVisible(map, lat, lng)
  const targetZoom = Math.max(zoom, preferredZoom)
  const desiredCenter = mapCenterForFocusAnchor(map, lat, lng, targetZoom, focusAnchor)

  if (!forceCenter && comfortable && zoom >= preferredZoom - 0.35) return

  if (forceCenter) {
    const center = map.getCenter()
    // Pixel tolerance: project/unproject + focus-anchor math is rarely exact to 1e-5°.
    const a = map.latLngToContainerPoint(center)
    const b = map.latLngToContainerPoint(desiredCenter)
    if (a.distanceTo(b) < 2 && zoom >= preferredZoom - 0.35) return
  }

  map.stop()
  markProgrammatic(map)

  if (zoom >= preferredZoom) {
    map.panTo(desiredCenter, { animate: true, duration: 0.45 })
    return
  }

  map.flyTo(desiredCenter, preferredZoom, { duration: 0.55 })
}

/**
 * Soft follow for moving GPS pins — pan only if the point leaves the frame.
 * Never changes zoom; skips entirely after the user pans/zooms.
 */
export function softFollowPoint(
  map: L.Map,
  lat: number,
  lng: number,
  opts?: { focusAnchor?: CameraFocusAnchor },
): void {
  if (userHasAdjustedCamera()) return
  if (isPointComfortablyVisible(map, lat, lng, -0.12)) return
  map.stop()
  markProgrammatic(map)
  const focusAnchor = opts?.focusAnchor ?? { x: 0.5, y: 0.5 }
  const center = mapCenterForFocusAnchor(map, lat, lng, map.getZoom(), focusAnchor)
  map.panTo(center, { animate: true, duration: 0.4 })
}

/**
 * Fit bounds for area entities / multi-point results.
 * Skips only when the target is already fully on screen (no user-gesture gate —
 * selection changes always clear camera intent and should be allowed to re-frame).
 */
export function ensureBoundsVisible(
  map: L.Map,
  bounds: L.LatLngBounds,
  opts?: { maxZoom?: number; padding?: L.PointExpression },
): void {
  if (!bounds.isValid()) return

  const maxZoom = opts?.maxZoom ?? 12
  const padding = opts?.padding ?? [48, 48]
  const current = map.getBounds()

  // Already framing the target — leave the view alone.
  if (current.contains(bounds)) return

  forceFitBounds(map, bounds, { maxZoom, padding })
}

/** Always fit bounds (e.g. cluster click) — does not skip when already on screen. */
export function forceFitBounds(
  map: L.Map,
  bounds: L.LatLngBounds,
  opts?: {
    maxZoom?: number
    padding?: L.PointExpression
    paddingTopLeft?: L.PointExpression
    paddingBottomRight?: L.PointExpression
    duration?: number
  },
): void {
  if (!bounds.isValid()) return
  map.stop()
  markProgrammatic(map)
  const fitOpts: L.FitBoundsOptions = {
    maxZoom: opts?.maxZoom ?? 18,
    animate: true,
    duration: opts?.duration ?? 0.55,
  }
  if (opts?.paddingTopLeft || opts?.paddingBottomRight) {
    fitOpts.paddingTopLeft = opts.paddingTopLeft ?? [0, 0]
    fitOpts.paddingBottomRight = opts.paddingBottomRight ?? [0, 0]
  } else {
    fitOpts.padding = opts?.padding ?? [48, 48]
  }
  map.fitBounds(bounds, fitOpts)
}

/** Programmatic setView that participates in the camera session. */
export function forceSetView(
  map: L.Map,
  lat: number,
  lng: number,
  zoom: number,
  opts?: { duration?: number; focusAnchor?: CameraFocusAnchor },
): void {
  map.stop()
  markProgrammatic(map)
  const focusAnchor = opts?.focusAnchor ?? { x: 0.5, y: 0.5 }
  const center = mapCenterForFocusAnchor(map, lat, lng, zoom, focusAnchor)
  map.setView(center, zoom, {
    animate: true,
    duration: opts?.duration ?? 0.55,
  })
}
